import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import { Skeleton } from 'primereact/skeleton';

import { ReactComponent as CollapsedIcon } from 'assets/svg/CollapsedIcon.svg';
import { ReactComponent as ExpandedIcon } from 'assets/svg/ExpandedIcon.svg';
import { useCalendarState, useCalendarActions, useCalendarUI } from '../../context/CalendarContext';
import { useUserList } from '../../hooks/useUserList';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import ImageComponent from 'shared/ImageComponent';
import { useDebounce } from 'hooks/useDebounce';
import { getTenantDetails } from 'utils/utils';

const UserSidebar = () => {
  const userContext = useUserDetailsContext();
  const { selectedUsers, googleEventsEnabled, isGlobal } = useCalendarState();
  const { setUsers, toggleGoogleEvents, clearGoogleEvents } = useCalendarActions();
  const { sidebarCollapsed, toggleSidebar } = useCalendarUI();

  const [search, setSearch] = useState('');
  const { addToast } = useToast();
  const [tenantId] = getTenantDetails();

  const searchDebounced = useDebounce(search, 500);

  // Local state for pending selections (not applied yet)
  const [pendingSelectedUsers, setPendingSelectedUsers] = useState(selectedUsers);
  const [pendingGoogleEvents, setPendingGoogleEvents] = useState(googleEventsEnabled);

  // Fetch users with pagination
  const {
    users,
    selectedUsers: selectedUsersList,
    isLoading,
    hasMore,
    loadMore,
    isLoadingMore,
  } = useUserList({
    search: searchDebounced,
    limit: 20,
    enabled: isGlobal,
    selectedUsersIds: selectedUsers,
  });

  // Sync pending state with context when it changes from outside
  useEffect(() => {
    setPendingSelectedUsers(selectedUsers);
  }, [selectedUsers]);

  useEffect(() => {
    setPendingGoogleEvents(googleEventsEnabled);
  }, [googleEventsEnabled]);

  const selectedUserObjects = useMemo(() => {
    const selectedSet = new Set(pendingSelectedUsers);

    // Combine and deduplicate users based on firm_user_id
    const combinedUsers = [...(users || []), ...(selectedUsersList || [])];
    const uniqueUsersMap = new Map();

    combinedUsers.forEach((user) => {
      const userId = user.firm_user_id;
      if (userId && !uniqueUsersMap.has(userId)) {
        uniqueUsersMap.set(userId, user);
      }
    });

    // Filter only selected users
    return Array.from(uniqueUsersMap.values()).filter((user) => {
      return selectedSet.has(user.firm_user_id);
    });
  }, [users, selectedUsersList, pendingSelectedUsers]);

  const handleScroll = useCallback(
    (values) => {
      if (values.top > 0.9 && hasMore && !isLoadingMore) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore]
  );

  const handleClear = useCallback(() => {
    // Clear pending selections
    setPendingSelectedUsers([]);
    setPendingGoogleEvents(new Set());

    // Apply to context (triggers API call)
    setUsers([userContext?.userDetails?.firm_user_id]);
    clearGoogleEvents();
  }, [setUsers, clearGoogleEvents]);

  const handleUpdate = useCallback(() => {
    // Apply pending selections to context (triggers API call)
    setUsers(pendingSelectedUsers);

    // Apply pending Google events
    const currentGoogleEvents = new Set(googleEventsEnabled);
    pendingGoogleEvents.forEach((userId) => {
      if (!currentGoogleEvents.has(userId)) {
        toggleGoogleEvents(userId);
      }
    });
    // Remove Google events that were unchecked
    currentGoogleEvents.forEach((userId) => {
      if (!pendingGoogleEvents.has(userId)) {
        toggleGoogleEvents(userId);
      }
    });
  }, [pendingSelectedUsers, googleEventsEnabled, pendingGoogleEvents, setUsers, toggleGoogleEvents, addToast]);

  const handleUserToggle = useCallback((userId) => {
    // Toggle in local pending state only
    setPendingSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        // If unchecking user, also remove from pending Google events
        setPendingGoogleEvents((prevGoogle) => {
          const newSet = new Set(prevGoogle);
          newSet.delete(userId);
          return newSet;
        });
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  }, []);

  const handleGoogleToggle = useCallback(
    (userId, userName) => {
      // Toggle in local pending state only
      setPendingGoogleEvents((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(userId)) {
          newSet.delete(userId);
        } else {
          newSet.add(userId);
        }
        return newSet;
      });

      const isEnabled = pendingGoogleEvents.has(userId);
      addToast(false, toastConstant.toasterType.INFO, 'Google Events', `${isEnabled ? 'Hidden' : 'Selected'} for ${userName}`);
    },
    [pendingGoogleEvents, addToast]
  );

  // Collapsed view (desktop only - on mobile, collapsed state hides the drawer)
  if (sidebarCollapsed) {
    return (
      <div className="calendar-sidebar-collapsed">
        <div className="calendar-sidebar-collapsed__button-wrapper" onClick={toggleSidebar}>
          <ExpandedIcon className="sidebar-icon-expand" />
          <Tooltip target=".team-calendars-collapsed-btn" content="Team Calendars" position="right" />
          <div className="team-calendars-collapsed-btn">
            <i className="pi pi-calendar sidebar-icon-calendar"></i>
          </div>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="calendar-sidebar-expanded p-2 pt-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 sidebar-header">
        <div className="d-flex align-items-center">
          <i className="pi pi-calendar me-2 sidebar-header-icon"></i>
          <h5 className="mb-0 sidebar-header-title">Team Calendars</h5>
        </div>
        {/* Close button - visible on mobile and as collapse on desktop */}
        {/* Collapse button (desktop only) */}
        <span
          role="button"
          tabIndex={0}
          onClick={toggleSidebar}
          aria-label="Collapse sidebar"
          className="sidebar-close-btn d-none d-lg-flex align-items-center cursor-pointer"
        >
          <CollapsedIcon className="desktop-collapse-icon sidebar-icon-collapse" />
        </span>
      </div>

      {/* Selected users summary */}
      <div className="mb-3 pb-3 border-bottom sidebar-summary">
        {isLoading ? (
          <>
            <div className="d-flex align-items-center mb-3">
              <Skeleton shape="circle" size="2.2rem" className="me-n2" />
              <Skeleton shape="circle" size="2.2rem" className="me-n2" style={{ zIndex: 1 }} />
              <Skeleton shape="circle" size="2.2rem" className="me-2" style={{ zIndex: 2 }} />
              <Skeleton width="5rem" height="1rem" />
            </div>
            <div className="d-flex gap-2">
              <Skeleton width="100%" height="2.2rem" borderRadius="6px" />
              <Skeleton width="100%" height="2.2rem" borderRadius="6px" />
            </div>
          </>
        ) : selectedUserObjects.length > 0 ? (
          <>
            <div className="d-flex align-items-center mb-2 flex-wrap">
              {selectedUserObjects.slice(0, 3).map((user, index) => {
                const color = user?.calendar_color || '#9c27b0';
                const userId = user?.firm_user_id;
                return (
                  <ImageComponent
                    key={userId}
                    filePath={userId ? `${tenantId}/${userId}/profile_image` : null}
                    fileName={user?.first_name}
                    className={`selected-user-${userId}`}
                    style={{
                      backgroundColor: color,
                      border: `2px solid ${color}`,
                      marginLeft: index > 0 ? '-8px' : '0',
                      zIndex: selectedUserObjects.length - index,
                    }}
                    toolTipContent={generateFullName(user)}
                    showTooltip
                    toolTipTarget={`.selected-user-${userId}`}
                  />
                );
              })}
              {selectedUserObjects.length > 3 && (
                <Avatar
                  label={`+${selectedUserObjects.length - 3}`}
                  shape="circle"
                  style={{
                    backgroundColor: '#9c27b0',
                    marginLeft: '-8px',
                    zIndex: 0,
                  }}
                  size="normal"
                  className="p-3 "
                />
              )}
              <span className="F-size12 text-muted ms-2">{selectedUserObjects.length} selected</span>
            </div>

            {selectedUserObjects.length > 6 && (
              <div className="mt-2 mb-2 p-2 rounded sidebar-warning">
                <i className="pi pi-exclamation-circle me-2 sidebar-warning-icon"></i>
                <span className="F-size12 sidebar-warning-text">
                  Loading {selectedUserObjects.length} calendars may take additional time.
                </span>
              </div>
            )}
          </>
        ) : null}

        {!isLoading && (
          <div className="d-flex gap-2 sidebar-actions mt-3">
            <Button label="Reset" className="d-flex p-button p-button-secondary outline" onClick={handleClear} />
            {selectedUserObjects.length === 0 ? (
              <Tooltip target=".update-button-wrapper" content="Select at least one user" position="top" />
            ) : null}
            <span className="update-button-wrapper sidebar-update-wrapper">
              <Button
                label="Update"
                className="p-button p-button-secondary"
                onClick={handleUpdate}
                disabled={selectedUserObjects.length === 0}
                style={{ width: '100%' }}
              />
            </span>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="mb-3 sidebar-search">
        <span className="p-input-icon-left w-100">
          <i className="pi pi-search" />
          <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members..." className="w-100" />
        </span>
      </div>

      {/* User list with infinite scroll */}
      <Scrollbars className="sidebar-scroll" autoHide autoHideTimeout={1000} onScrollFrame={handleScroll}>
        {isLoading ? (
          <div className="user-list-skeleton">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="skeleton-user-row">
                <div className="d-flex align-items-center flex-grow-1 gap-2">
                  <Skeleton shape="circle" size="8px" />
                  <Skeleton width="65%" height="1rem" />
                </div>
                <Skeleton shape="circle" size="1.25rem" />
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="text-center text-muted py-4 F-size14">No members found</div>
        ) : (
          <>
            <div style={{ marginBottom: '8px' }}>
              {users.map((user) => {
                const userId = user.firm_user_id || user.user_id;
                const isSelected = pendingSelectedUsers.includes(userId);
                const fullName = generateFullName(user);
                const color = user.calendar_color || '#9c27b0';

                return (
                  <div
                    key={userId}
                    className={`d-flex align-items-center justify-content-between cursor-pointer rounded sidebar-user-row ${
                      isSelected ? 'is-selected' : ''
                    }`}
                    onClick={(e) => handleUserToggle(userId)}
                  >
                    <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                      <div
                        className="rounded-circle me-2 sidebar-user-dot"
                        style={{
                          backgroundColor: color,
                          width: '10px',
                          height: '10px',
                        }}
                      ></div>
                      <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                        <span className="F-size14" style={{ color: 'var(--black)' }}>
                          {fullName}
                        </span>
                      </div>
                      {/* {isSelected && (
                        <>
                          <div
                            className={`ms-2 cursor-pointer d-flex align-items-center justify-content-center google-calendar-icon-${userId} google-calendar-icon ${
                              pendingGoogleEvents.has(userId) ? 'active' : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGoogleToggle(userId, fullName);
                            }}
                          >
                            <span className="google-calendar-icon-label">G</span>
                          </div>
                          <Tooltip target={`.google-calendar-icon-${userId}`} content="Show Google Events" position="left" />
                        </>
                      )} */}
                    </div>
                    <Checkbox
                      inputId={`member-checkbox-${userId}`}
                      checked={isSelected}
                      // onChange={(e) => handleUserToggle(userId)}
                      className="ms-2"
                    />
                  </div>
                );
              })}
              {isLoadingMore && (
                <div className="text-center py-2">
                  <i className="pi pi-spin pi-spinner"></i>
                </div>
              )}
              {hasMore && !isLoadingMore && <div className="text-center py-2 text-muted F-size12">Scroll for more...</div>}
            </div>
          </>
        )}
      </Scrollbars>
    </div>
  );
};

// Wrap in React.memo for better performance - prevents unnecessary rerenders
export default React.memo(UserSidebar);
